import { BaseFetcher, FetcherMeta } from '@Types/fetcher'
import { useState } from 'react'
import { CacheState, useCacheActions } from '@Store/slices/cache'

// NOTE: Unlike `useBaseFetcher`, this hook does NOT rethrow errors in an
// effect to bubble them up to the router/ErrorBoundary. Failures (HTTP
// non-2xx, missing bridge, network) are recorded on `meta.isError` /
// `meta.error` only. Callers MUST inspect `meta.isError` and render their
// own error UI — otherwise failures will silently park the consumer on
// loading/empty state. This intentional asymmetry exists because the
// current caller (`AppConnectGuard`) needs to render a recoverable,
// feature-specific error view rather than tripping the global boundary.
function useShopifyFetcher(): BaseFetcher {
  const [meta, setMeta] = useState<FetcherMeta>({
    isInitialized: false,
    isFinished: false,
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: null,
    data: null,
    url: '',
    options: {},
  })

  const { invalidate } = useCacheActions()

  const fetcher = async (
    url: string,
    options: RequestInit,
    callback?: (...args: any[]) => void,
    invalidateCacheKeys?: (keyof CacheState)[],
  ) => {
    setMeta((prev) => ({
      ...prev,
      isInitialized: true,
      isFinished: false,
      isLoading: true,
      isSuccess: false,
      isError: false,
      error: null,
      data: null,
      url,
      options,
    }))

    try {
      if (!window.shopify)
        throw new Error('Shopify bridge (window.shopify) is not available')

      const applyAuth = async (opts: RequestInit): Promise<RequestInit> => {
        const idToken = await window.shopify.idToken()
        return {
          ...opts,
          headers: {
            ...(opts.headers ?? {}),
            Authorization: `Bearer ${idToken}`,
          },
        }
      }

      options = await applyAuth(options ?? { headers: {} })

      let response = await fetch(url, options)

      // Single-shot retry with a fresh ID token if the backend rejects the
      // current one. `window.shopify.idToken()` mints a new short-lived token
      // each call, so a fresh attempt is enough; no refresh-token dance.
      if (response.status === 401) {
        options = await applyAuth(options)
        response = await fetch(url, options)
      }

      const hasError = !response.ok
      let result
      try {
        result = await response.json()
      } catch {
        result = null
      }

      if (callback) {
        callback(result, hasError)
      }

      if (!hasError && invalidateCacheKeys && invalidateCacheKeys.length > 0) {
        invalidate(invalidateCacheKeys)
      }

      setMeta((prev) => ({
        ...prev,
        isLoading: false,
        isFinished: true,
        isSuccess: !hasError,
        isError: hasError,
        error: hasError ? result : null,
        data: hasError ? null : result,
      }))
    } catch (error) {
      setMeta((prev) => ({
        ...prev,
        isLoading: false,
        isFinished: true,
        isSuccess: false,
        isError: true,
        error: error as Error,
        // Clear stale data from any prior successful call so callers
        // gating on `data` don't read previous-request results.
        data: null,
      }))
    }
  }

  return [fetcher, { ...meta }, setMeta]
}

export default useShopifyFetcher
